import { createFileRoute, redirect } from "@tanstack/react-router";
import * as z from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { HTTPError } from "ky";
import type { NavigateOptions } from "@tanstack/react-router";
import { LoadingSpinner } from "@/components/loading";
import { __internal_router } from "@/main";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { api } from "@/lib/http";
import { takeZoomOAuthReturnTo } from "@/lib/utils/zoom-connection";
import { setFlashMessage } from "@/lib/utils/flash";

const zoomCallbackSchema = z.object({
  code: z.string().min(1),
});

export const Route = createFileRoute("/_app/callback/zoom")({
  validateSearch: zodValidator(zoomCallbackSchema),
  loaderDeps: ({ search: { code } }) => ({ code }),
  async loader({ context: { queryClient }, deps: { code } }) {
    const returnTo = takeZoomOAuthReturnTo();
    const destinationOptions: NavigateOptions = returnTo
      ? ({ to: returnTo } as NavigateOptions)
      : { to: "/linked-accounts", search: { tab: "account" } };
    const preloadPromise = __internal_router.preloadRoute(destinationOptions);

    const userInfo = await queryClient.ensureQueryData(userInfoQueryOptions);

    try {
      await api.processing.raw.post("api/v1/zoom/oauth/callback", {
        json: {
          code,
          name: userInfo.full_name,
        },
      });
    } catch (error) {
      if (!(error instanceof HTTPError) || error.response.status !== 409) {
        throw error;
      }
      // Zoom skips consent for an already-authorized app and bounces straight
      // back, so a duplicate is reachable without the user meaning to retry.
      setFlashMessage({
        type: "error",
        text: "This Zoom account is already connected to an account.",
      });
      throw redirect({ to: "/linked-accounts", search: { tab: "account" } });
    }

    await queryClient.invalidateQueries({ queryKey: ["zoom-connections"] });
    setFlashMessage({ type: "success", text: "Zoom account connected" });
    await preloadPromise;
    throw redirect(destinationOptions);
  },
  component: () => (
    <span>You are being redirected back to the connections page</span>
  ),
  pendingComponent: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="flex size-full items-center align-middle">
      <LoadingSpinner />
    </div>
  );
}
